import { useIsMobile } from '@/hooks/use-mobile';
import { AnnualHeatmapCell } from './AnnualHeatmapCell';
import type { AnnualLocationData } from '@/data/annual-calendar/types';

interface AnnualHeatmapTableProps {
  locations: AnnualLocationData[];
  saturdays: string[];
  onLocationClick?: (locationId: string) => void;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function getMonthSpans(saturdays: string[]): { month: string; count: number }[] {
  const spans: { month: string; count: number }[] = [];
  let currentMonth = '';
  for (const label of saturdays) {
    const month = label.split(' ')[0];
    if (month !== currentMonth) {
      spans.push({ month, count: 1 });
      currentMonth = month;
    } else {
      spans[spans.length - 1].count++;
    }
  }
  return spans;
}

export const AnnualHeatmapTable: React.FC<AnnualHeatmapTableProps> = ({ locations, saturdays, onLocationClick }) => {
  const isMobile = useIsMobile();
  const monthSpans = getMonthSpans(saturdays);

  if (isMobile) {
    // Group cells by month for each location
    const monthIndices: { month: string; start: number; end: number }[] = [];
    let currentMonth = '';
    for (let i = 0; i < saturdays.length; i++) {
      const month = saturdays[i].split(' ')[0];
      if (month !== currentMonth) {
        monthIndices.push({ month, start: i, end: i });
        currentMonth = month;
      } else {
        monthIndices[monthIndices.length - 1].end = i;
      }
    }

    function bestRatingForMonth(cells: typeof locations[0]['cells'], start: number, end: number): number {
      let best = 0;
      for (let i = start; i <= end; i++) {
        if (cells[i].rating > best) best = cells[i].rating;
      }
      return best;
    }

    function getRatingBg(rating: number): string {
      if (rating >= 8) return 'bg-emerald-600';
      if (rating >= 6) return 'bg-green-500';
      if (rating >= 4) return 'bg-lime-400';
      if (rating >= 2) return 'bg-amber-300';
      return 'bg-muted/30';
    }

    return (
      <div className="rounded-lg border border-border/50 overflow-x-auto">
        <table className="border-collapse w-full" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '100px' }} />
            {monthIndices.map((_, i) => (
              <col key={i} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-background px-2 py-1.5 text-left text-[10px] font-medium text-muted-foreground border-b border-border/50">
                Location
              </th>
              {monthIndices.map((m) => (
                <th
                  key={m.month}
                  className="px-0 py-1.5 text-center text-[10px] font-medium text-muted-foreground border-b border-border/50"
                >
                  {m.month.slice(0, 3)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {locations.map((loc) => (
              <tr key={loc.id} className="group">
                <td className="sticky left-0 z-10 bg-background px-2 py-1 border-b border-border/30 group-hover:bg-secondary/30 transition-colors">
                  <button
                    onClick={() => onLocationClick?.(loc.id)}
                    className="text-[11px] font-medium text-foreground whitespace-nowrap hover:text-gold transition-colors truncate block max-w-[96px]"
                  >
                    {loc.name}
                  </button>
                </td>
                {monthIndices.map((m) => {
                  const best = bestRatingForMonth(loc.cells, m.start, m.end);
                  return (
                    <td key={m.month} className="px-0.5 py-1 border-b border-border/30">
                      <div className={`w-full h-4 rounded-[2px] ${getRatingBg(best)} border border-black/10`} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border/50">
      <table className="border-collapse w-full" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '150px' }} />
          {saturdays.map((_, i) => (
            <col key={i} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-background px-2 py-1.5 text-left text-xs font-medium text-muted-foreground border-b border-border/50">
              Location
            </th>
            {monthSpans.map((span) => (
              <th
                key={span.month}
                colSpan={span.count}
                className="px-0 py-1.5 text-center text-[10px] font-medium text-muted-foreground border-b border-border/50"
              >
                {span.month}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {locations.map((loc) => (
            <tr key={loc.id} className="group">
              <td className="sticky left-0 z-10 bg-background px-2 py-1 border-b border-border/30 group-hover:bg-secondary/30 transition-colors">
                <button onClick={() => onLocationClick?.(loc.id)} className="text-xs font-medium text-foreground whitespace-nowrap hover:text-gold transition-colors">{loc.name}</button>
              </td>
              {loc.cells.map((cell, i) => (
                <td key={i} className="px-0 py-1 border-b border-border/30">
                  <AnnualHeatmapCell
                    rating={cell.rating}
                    date={saturdays[i]}
                    moonPercent={cell.moonPercent}
                    gcHours={cell.gcHours}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
